import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isOrgOwner?: boolean;
  assignedBranches: Array<{
    branchId: number;
    branchName: string;
    branchCode: string;
  }>;
}

interface ModulePermission {
  moduleId: number;
  moduleName: string;
  moduleDisplayName: string;
  description: string;
  availablePermissions: string[];
  isTasksModule?: boolean; // Flag to identify tasks module
  permissions: {
    noAccess: boolean;
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
    fullAccess: boolean;
    // Tasks module specific permissions
    viewMyTasksOnly?: boolean;
    viewAllTasks?: boolean;
  };
}

const RoleManagement: React.FC = () => {
  const { user } = useAuth();
  const { currentBranch } = useBranch();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const [modulePermissions, setModulePermissions] = useState<ModulePermission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmationText, setConfirmationText] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('leadshive_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';
      
      const response = await fetch(`${API_BASE_URL}/role-management/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = async (user: User) => {
    setSelectedUser(user);
    setSelectedBranch(null);
    setModulePermissions([]);
  };

  const handleBranchSelect = async (branch: any) => {
    if (!selectedUser) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('leadshive_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';
      
      console.log('Selected branch:', branch);
      console.log('Branch ID:', branch.branchId || branch.id);
      
      const branchId = branch.branchId || branch.id;
      if (!branchId) {
        setError('Branch ID not found');
        return;
      }
      
      const response = await fetch(
        `${API_BASE_URL}/role-management/users/${selectedUser.id}/branches/${branchId}/permissions`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
    console.log('API Response:', data);
    console.log('Module Permissions:', data.modulePermissions);
    // Log tasks module specifically
    const tasksModule = data.modulePermissions.find((m: ModulePermission) => m.moduleName === 'tasks');
    console.log('Tasks Module:', tasksModule);
    setModulePermissions(data.modulePermissions);
    setSelectedBranch(branch);
      } else {
        const errorData = await response.json();
        console.error('API Error:', errorData);
        setError(errorData.error || 'Failed to fetch permissions');
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setError('Failed to fetch permissions');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (moduleId: number, permission: string, value: boolean) => {
    setModulePermissions(prev => 
      prev.map(module => {
        if (module.moduleId === moduleId) {
          const newPermissions = { ...module.permissions };
          
          // Handle Tasks module specific permissions
          if (permission === 'viewMyTasksOnly' || permission === 'viewAllTasks') {
            newPermissions[permission as keyof typeof newPermissions] = value;
            
            // If user checks "View My Tasks Only", uncheck "View All Tasks" and vice versa
            if (permission === 'viewMyTasksOnly' && value) {
              newPermissions.viewAllTasks = false;
            }
            if (permission === 'viewAllTasks' && value) {
              newPermissions.viewMyTasksOnly = false;
            }
          } else if (permission === 'noAccess') {
            // If no access is selected, uncheck all others
            if (value) {
              newPermissions.noAccess = true;
              newPermissions.canView = false;
              newPermissions.canCreate = false;
              newPermissions.canEdit = false;
              newPermissions.canDelete = false;
              newPermissions.fullAccess = false;
            } else {
              newPermissions.noAccess = false;
            }
          } else if (permission === 'fullAccess') {
            // If full access is selected, uncheck no access
            if (value) {
              newPermissions.fullAccess = true;
              newPermissions.noAccess = false;
            } else {
              newPermissions.fullAccess = false;
            }
          } else {
            // For other permissions, uncheck no access and full access
            newPermissions[permission as keyof typeof newPermissions] = value;
            if (value) {
              newPermissions.noAccess = false;
              newPermissions.fullAccess = false;
            }
          }
          
          return {
            ...module,
            permissions: newPermissions
          };
        }
        return module;
      })
    );
  };

  const handleSavePermissions = async () => {
    if (!selectedUser || !selectedBranch) return;
    
    if (confirmationText !== 'confirm') {
      setError('Please type "confirm" to proceed with permission changes');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('leadshive_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';
      
      const permissions = modulePermissions.map(module => {
        const permissionData: any = {
          moduleId: module.moduleId,
          noAccess: module.permissions.noAccess,
          canView: module.permissions.canView,
          canCreate: module.permissions.canCreate,
          canEdit: module.permissions.canEdit,
          canDelete: module.permissions.canDelete,
          fullAccess: module.permissions.fullAccess
        };

        // Add Tasks module specific permissions
        if (module.isTasksModule) {
          permissionData.viewMyTasksOnly = module.permissions.viewMyTasksOnly || false;
          permissionData.viewAllTasks = module.permissions.viewAllTasks !== undefined ? module.permissions.viewAllTasks : true;
        }

        return permissionData;
      });

      const response = await fetch(
        `${API_BASE_URL}/role-management/users/${selectedUser.id}/branches/${selectedBranch.branchId}/permissions`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            permissions,
            confirmationText
          })
        }
      );

      if (response.ok) {
        setError(null);
        setConfirmationText('');
        // Refresh permissions
        await handleBranchSelect(selectedBranch);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update permissions');
      }
    } catch (error) {
      console.error('Error updating permissions:', error);
      setError('Failed to update permissions');
    } finally {
      setLoading(false);
    }
  };

  const getPermissionLabel = (permission: string) => {
    const labels: { [key: string]: string } = {
      noAccess: 'No Access',
      canView: 'View',
      canCreate: 'Create',
      canEdit: 'Edit',
      canDelete: 'Delete',
      fullAccess: 'Full Access'
    };
    return labels[permission] || permission;
  };

  const getPermissionDescription = (permission: string) => {
    const descriptions: { [key: string]: string } = {
      noAccess: 'Module will be completely hidden from user',
      canView: 'User can see all data in this module',
      canCreate: 'User can create new items in this module',
      canEdit: 'User can modify existing items in this module',
      canDelete: 'User can remove items from this module',
      fullAccess: 'User has all permissions for this module'
    };
    return descriptions[permission] || '';
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
            <div className="mt-4">
              <button
                onClick={() => setError(null)}
                className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Role Management</h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage user permissions for different modules within specific branches
          </p>
        </div>

        <div className="p-6">
          {!selectedUser ? (
            // User Selection
            <div>
              <h3 className="text-md font-medium text-gray-900 mb-4">Select User</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {users.filter(user => user.role !== 'admin' && !user.isOrgOwner).map((user) => (
                  <div
                    key={user.id}
                    onClick={() => handleUserSelect(user)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md cursor-pointer transition-all"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-blue-600">
                            {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-sm text-gray-500 truncate">{user.email}</p>
                        <p className="text-xs text-gray-400 capitalize">{user.role}</p>
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">
                        {user.assignedBranches.length} branch(es) assigned
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : !selectedBranch ? (
            // Branch Selection
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-md font-medium text-gray-900">
                  Select Branch for {selectedUser.firstName} {selectedUser.lastName}
                </h3>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  ← Back to Users
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedUser.assignedBranches.map((branch) => (
                  <div
                    key={branch.branchId}
                    onClick={() => handleBranchSelect(branch)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md cursor-pointer transition-all"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-green-600">
                            {branch.branchCode}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{branch.branchName}</p>
                        <p className="text-sm text-gray-500">Code: {branch.branchCode}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Permission Management
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-md font-medium text-gray-900">
                    Manage Permissions for {selectedUser.firstName} {selectedUser.lastName}
                  </h3>
                  <p className="text-sm text-gray-500">Branch: {selectedBranch.branchName}</p>
                </div>
                <button
                  onClick={() => setSelectedBranch(null)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  ← Back to Branches
                </button>
              </div>

              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {modulePermissions.map((module) => (
                    <div key={module.moduleId} className="border border-gray-200 rounded-lg p-4">
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-900">
                          {module.moduleDisplayName}
                        </h4>
                        {module.description && (
                          <p className="text-xs text-gray-500 mt-1">{module.description}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {module.availablePermissions.map((permission) => (
                          <div key={permission} className="flex items-start space-x-3">
                            <div className="flex items-center h-5">
                              <input
                                type="checkbox"
                                checked={module.permissions[permission as keyof typeof module.permissions] || false}
                                onChange={(e) => handlePermissionChange(module.moduleId, permission, e.target.checked)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            </div>
                            <div className="text-sm">
                              <label className="font-medium text-gray-700">
                                {getPermissionLabel(permission)}
                              </label>
                              <p className="text-xs text-gray-500">
                                {getPermissionDescription(permission)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Tasks Module Specific Permissions */}
                      {module.isTasksModule && !module.permissions.noAccess && (
                        <div className="mt-6 pt-4 border-t border-gray-200">
                          <h5 className="text-sm font-semibold text-gray-900 mb-3">
                            Task View Options
                          </h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-start space-x-3">
                              <div className="flex items-center h-5">
                                <input
                                  type="checkbox"
                                  checked={module.permissions.viewMyTasksOnly || false}
                                  onChange={(e) => handlePermissionChange(module.moduleId, 'viewMyTasksOnly', e.target.checked)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                              </div>
                              <div className="text-sm">
                                <label className="font-medium text-gray-700">
                                  View My Tasks Only
                                </label>
                                <p className="text-xs text-gray-500">
                                  User can only see their assigned tasks
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start space-x-3">
                              <div className="flex items-center h-5">
                                <input
                                  type="checkbox"
                                  checked={module.permissions.viewAllTasks || false}
                                  onChange={(e) => handlePermissionChange(module.moduleId, 'viewAllTasks', e.target.checked)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                              </div>
                              <div className="text-sm">
                                <label className="font-medium text-gray-700">
                                  View All Tasks
                                </label>
                                <p className="text-xs text-gray-500">
                                  User can see both All Tasks and My Tasks tabs
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Confirmation and Save */}
                  <div className="border-t border-gray-200 pt-6">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Type "confirm" to proceed with permission changes
                      </label>
                      <input
                        type="text"
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Type 'confirm' here"
                      />
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => setSelectedBranch(null)}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSavePermissions}
                        disabled={loading || confirmationText !== 'confirm'}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Saving...' : 'Save Permissions'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleManagement;
